import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import { AddRounded, CancelRounded, ExpandMore } from '@mui/icons-material';
import { CreativeRadio, RadioOptionGroup } from '@/shared/ui';
import { SkillLevelsArraySchema } from '../../types';
import { Instruments } from '@/shared/i18n';
import { InstrumentsLoading, useInstrumentsStore } from '../../store/instruments-store';

interface AddInstrumentDialogProps {
  open: boolean;
  onClose: () => void;
  dict: Instruments;
  onConfirm: (
    selectedOption: string | undefined,
    isUpdating: boolean,
    resetForm: () => void,
  ) => void;
  name?: string;
  imageSrc?: string;
  selectedValue?: string;
  selectedOption?: string;
  skillLevelsOptions: SkillLevelsArraySchema;
  isUpdating: boolean;
}

const levelOptionsMap = (dict: Instruments) => [
  {
    name: dict.instruments.instrument_no_experience,
    imageSrc: '/assets/images/onboarding/percorso-no-strumento.svg',
    value: 'Sono agli inizi',
  },
  {
    name: dict.instruments.instrument_some_experience,
    imageSrc: '/assets/images/onboarding/percorso-si-strumento.svg',
    value: 'Ho già esperienza',
  },
];

const AddInstrumentDialog: React.FC<AddInstrumentDialogProps> = ({
  open,
  onClose,
  onConfirm,
  name,
  imageSrc,
  selectedOption: initialSelectedOption,
  skillLevelsOptions,
  dict,
  isUpdating,
}) => {
  const { isLoading: isLoadingStore } = useInstrumentsStore();
  const isLoading =
    isLoadingStore === InstrumentsLoading.CREATE || isLoadingStore === InstrumentsLoading.UPDATE;

  const [isAlreadySelected, setIsAlreadySelected] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>();
  const [selectedValue, setSelectedValue] = useState<string>();

  const levelOptions = React.useMemo(() => levelOptionsMap(dict), [dict]);

  const handleRadioChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedValue(event.target.value);
      if (event.target.value === levelOptions[1].name) {
        setIsAlreadySelected(true);
      } else {
        // "Da zero"
        setSelectedOption(skillLevelsOptions[0].name);
        setIsAlreadySelected(false);
      }
    },
    [skillLevelsOptions, levelOptions],
  );

  const resetForm = React.useCallback(() => {
    setSelectedOption(undefined);
    setSelectedValue(undefined);
    setIsAlreadySelected(false);
  }, []);

  const handleOptionChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  }, []);

  const handleOnClose = React.useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleConfirm = React.useCallback(() => {
    if (selectedOption !== undefined && !!name) {
      const skillOptionName = skillLevelsOptions.find(
        (option) => option.onboardingDescription === selectedOption,
      )?.name;
      onConfirm(skillOptionName || skillLevelsOptions[0].name, isUpdating, () => {
        resetForm();
      });
    }
  }, [onConfirm, selectedOption, isUpdating, name, skillLevelsOptions, resetForm]);

  React.useEffect(() => {
    if (initialSelectedOption) {
      if (initialSelectedOption === skillLevelsOptions[0].name) {
        setSelectedValue(levelOptions[0].name);
      } else {
        setSelectedValue(levelOptions[1].name);
        setIsAlreadySelected(true);
      }
      const option = skillLevelsOptions.find((option) => option.name === initialSelectedOption);
      if (!option) return;
      setSelectedOption(option.onboardingDescription);
    }
  }, [initialSelectedOption, skillLevelsOptions, levelOptions]);

  return (
    <Dialog open={open} onClose={handleOnClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex justify-between" component="div">
        <Typography variant="h5" className="text-text-primary">
          {isUpdating ? dict.instruments.change_auto_evaluation : 'Aggiungi strumento'}
        </Typography>
        <CancelRounded className="text-action-divider cursor-pointer" onClick={handleOnClose} />
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4">
          <Typography variant="body-500" color="textSecondary">
            {dict.instruments.choose_instrument_path}
          </Typography>
          <div className="flex items-center gap-x-4 py-2">
            <img src={imageSrc} alt={`icona ${name}`} className="size-8" />
            <Typography variant="h5" color="textPrimary">
              {name}
            </Typography>
          </div>
          {!isAlreadySelected && (
            <div className="grid grid-cols-2 gap-4 w-full">
              {levelOptions.map((levelOption) => (
                <CreativeRadio
                  key={levelOption.value}
                  imageSrc={levelOption.imageSrc}
                  altText={levelOption.value}
                  name={levelOption.name}
                  value={levelOption.value}
                  checked={selectedValue === levelOption.value}
                  onChange={handleRadioChange}
                />
              ))}
            </div>
          )}
          {(isAlreadySelected || selectedValue === levelOptions[1].name) && (
            <>
              <Accordion className="rounded-xl">
                <AccordionSummary
                  expandIcon={<ExpandMore color="primary" />}
                  aria-controls="panel-content"
                  id="panel-header"
                >
                  <Typography variant="h6">{selectedValue}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="grid grid-cols-2 gap-4 w-full">
                    {levelOptions.map((option) => (
                      <CreativeRadio
                        key={option.value}
                        imageSrc={option.imageSrc}
                        altText={option.value}
                        name={option.name}
                        value={option.value}
                        checked={selectedValue === option.value}
                        onChange={handleRadioChange}
                      />
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
              {selectedValue === levelOptions[1].name && (
                <div className="pt-8 space-y-6">
                  <Typography
                    variant="s1"
                    color="textSecondary"
                    className="text-center"
                    component="h4"
                  >
                    {dict.instruments.choose_phrase}
                  </Typography>
                  <RadioOptionGroup
                    options={skillLevelsOptions
                      .slice(1)
                      .map((option) => option.onboardingDescription)}
                    value={selectedOption}
                    onChange={handleOptionChange}
                    labelPlacement="start"
                    labelClass="font-bold"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          <Typography variant="button-m">{dict.instruments.discard_changes}</Typography>
        </Button>
        <Button
          disabled={!selectedValue || isLoading}
          onClick={handleConfirm}
          color="primary"
          variant="contained"
        >
          {isLoading ? (
            <CircularProgress size={12} color="inherit" />
          ) : (
            <>
              {!isUpdating && <AddRounded />}
              <Typography variant="button-m">
                {isUpdating
                  ? dict.instruments.confirm_changes
                  : dict.instruments.add_instrument_dialog}
              </Typography>
            </>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddInstrumentDialog;
